<script setup lang="ts">
import { computed } from "vue";

import AvatarGroup from "~/components/dumb/AvatarGroup.vue";
import EntityPreviewTooltip from "~/components/dumb/EntityPreviewTooltip.vue";
import UpdateTime from "~/components/dumb/UpdateTime.vue";
import UserLink from "~/components/dumb/UserLink.vue";
import { DEACTIVATED_USER_LABEL } from "~/components/visualization/constants";
import { EditorMode, Placement } from "~/shared/enums";
import type { Doc } from "~/shared/types";
import { useDataStore } from "~/stores";
import { isString } from "~/utils/common";
import { getUserDisplayName } from "~/utils/user";

const { doc } = defineProps<{
  doc: Doc;
}>();

const dataStore = useDataStore();

const updatedBy = computed(() => (doc.updatedByDuid ? dataStore.getUserByDuid(doc.updatedByDuid) : undefined));
const createdBy = computed(() => (doc.createdByDuid ? dataStore.getUserByDuid(doc.createdByDuid) : undefined));

const actors = computed(() => [
  ...new Set([...doc.editorDuids, doc.createdByDuid, doc.updatedByDuid].filter(isString)),
]);
</script>

<template>
  <div class="flex flex-col gap-0.5 py-2 pr-2 pl-3 text-xs text-md select-none">
    <div class="mb-1 flex items-center gap-1">
      Edited by
      <AvatarGroup
        v-if="actors.length > 0"
        :duids="actors"
        :editor-mode="EditorMode.FOLDER"
        :limit="8"
        small
        tooltip-bottom
        link
        :unset-label="DEACTIVATED_USER_LABEL" />
    </div>
    <p class="flex flex-wrap">
      <span>Created&nbsp;</span>
      <UpdateTime :at="doc.createdAt" inherit-size inherit-color />
      <template v-if="createdBy">
        <span class="text-vlt">&nbsp;by&nbsp;</span>
        <EntityPreviewTooltip :user-duid="createdBy.duid" :placement="Placement.LEFT">
          <UserLink :user="createdBy">{{ getUserDisplayName(createdBy) }}</UserLink>
        </EntityPreviewTooltip>
      </template>
    </p>
    <p class="flex flex-wrap">
      <span>Last updated&nbsp;</span>
      <UpdateTime :at="doc.updatedAt" inherit-size inherit-color />
      <template v-if="updatedBy">
        <span class="text-vlt">&nbsp;by&nbsp;</span>
        <EntityPreviewTooltip :user-duid="updatedBy.duid" :placement="Placement.LEFT">
          <UserLink :user="updatedBy">{{ getUserDisplayName(updatedBy) }}</UserLink>
        </EntityPreviewTooltip>
      </template>
    </p>
  </div>
</template>
